import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import {
  EnvelopeSimple, Lock, ArrowLeft, ArrowRight, ShieldCheck, CheckCircle,
  Eye, EyeSlash,
} from "@phosphor-icons/react";

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success(data.message || t("request_sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell testId="forgot-password-page" title={sent ? t("pr_check_email_title") : t("pr_forgot_title")} subtitle={sent
      ? t("pr_check_email_subtitle")
      : t("pr_forgot_subtitle")}>
      {sent ? (
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#CCF22C] flex items-center justify-center mb-6">
            <CheckCircle size={32} weight="fill" className="text-[var(--tf-ink)]" />
          </div>
          <p className="text-xs text-[var(--tf-muted)] mb-6">{t("pr_no_email_hint")} <a href="mailto:info@tee.farm" className="underline text-[var(--tf-ink)]">info@tee.farm</a>.</p>
          <Link to="/login" className="tf-pill-secondary w-full" data-testid="forgot-back-to-login">
            <ArrowLeft size={14} weight="bold" /> {t("pr_back_to_login")}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="tf-label block mb-1.5">{t("email")}</label>
            <div className="relative">
              <EnvelopeSimple size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] pointer-events-none" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="tf-input !pl-11 !py-3.5"
                placeholder="tu@azienda.com"
                data-testid="forgot-email-input"
              />
            </div>
          </div>
          <button type="submit" disabled={loading} className="tf-pill-primary w-full" data-testid="forgot-submit">
            {loading ? t("pr_sending") : t("pr_send_reset_link")} <ArrowRight size={14} weight="bold" />
          </button>
          <Link to="/login" className="text-xs text-[var(--tf-muted)] hover:text-[var(--tf-ink)] flex items-center justify-center gap-1.5 pt-2" data-testid="forgot-back-link">
            <ArrowLeft size={12} weight="bold" /> {t("pr_back_to_login")}
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const strength = getPasswordStrength(password, t);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error(t("pr_password_too_short"));
    if (password !== confirm) return toast.error(t("pr_password_mismatch"));
    if (!token) return toast.error(t("pr_token_missing"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(t("pr_password_updated_toast"));
      setTimeout(() => nav("/login"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell testId="reset-password-invalid" title={t("pr_invalid_link_title")} subtitle={t("pr_invalid_link_desc")}>
        <Link to="/forgot-password" className="tf-pill-primary w-full">{t("pr_request_new_link")}</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell testId="reset-password-page" title={t("pr_new_password_title")} subtitle={t("pr_new_password_subtitle")}>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="tf-label block mb-1.5">{t("pr_new_password_label")}</label>
          <div className="relative">
            <Lock size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] pointer-events-none" />
            <input
              type={show ? "text" : "password"}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="tf-input !pl-11 !pr-11 !py-3.5"
              placeholder="••••••••"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tf-primary)]"
              aria-label={show ? t("pw_hide_label") : t("pw_show_label")}
              data-testid="reset-reveal-toggle"
            >
              {show ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
            </button>
          </div>
          {password && <PasswordStrengthMeter strength={strength} t={t} />}
        </div>
        <div>
          <label className="tf-label block mb-1.5">{t("pr_confirm_password_label")}</label>
          <div className="relative">
            <Lock size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] pointer-events-none" />
            <input
              type={show ? "text" : "password"}
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="tf-input !pl-11 !py-3.5"
              placeholder={t("pr_confirm_placeholder")}
              data-testid="reset-confirm-input"
            />
          </div>
          {confirm && confirm !== password && (
            <div className="text-xs text-[var(--tf-error)] mt-1.5">{t("pr_password_mismatch")}</div>
          )}
        </div>
        <button type="submit" disabled={loading || strength.score < 2} className="tf-pill-primary w-full" data-testid="reset-submit">
          {loading ? t("pr_updating") : t("pr_update_password")} <ArrowRight size={14} weight="bold" />
        </button>
      </form>
    </AuthShell>
  );
}

/* Password strength scoring — score 0=too weak, 1=weak, 2=medium, 3=strong, 4=very strong */
function getPasswordStrength(pw, t) {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  score = Math.min(score, 4);
  const labels = [t("pr_too_short"), t("pr_weak"), t("pr_medium"), t("pr_strong"), t("pr_very_strong")];
  const colors = ["#C4452E", "#C4452E", "#D4A017", "#4A8A3E", "#193529"];
  return { score, label: labels[score], color: colors[score] };
}

function PasswordStrengthMeter({ strength, t }) {
  return (
    <div className="mt-2.5" data-testid="password-strength-meter">
      <div className="flex gap-1 mb-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex-1 h-1 rounded-full transition-colors duration-300"
            style={{
              backgroundColor: i < strength.score ? strength.color : "var(--tf-border)",
            }}
            data-testid={`strength-bar-${i}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="uppercase tracking-widest text-[var(--tf-muted)]">{t("pr_security")}</span>
        <span className="font-bold" style={{ color: strength.color }} data-testid="strength-label">
          {strength.label}
        </span>
      </div>
      {strength.score < 3 && (
        <div className="text-[10px] text-[var(--tf-muted)] mt-1 leading-relaxed">
          {t("pr_hint")}
        </div>
      )}
    </div>
  );
}

function AuthShell({ children, title, subtitle, testId }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex flex-col bg-[var(--tf-bg)]" data-testid={testId}>
      <header className="px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="font-display font-black text-lg tracking-tight text-[var(--tf-ink)]">Teefarm Connect</Link>
        <div className="text-xs text-[var(--tf-muted)] hidden md:block">
          {t("login_help_needed")} <a href="mailto:info@tee.farm" className="underline text-[var(--tf-ink)]">info@tee.farm</a>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md bg-white border border-[var(--tf-border)] rounded-[24px] p-8 md:p-10">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[var(--tf-ink)]">{title}</h1>
          {subtitle && <p className="text-sm text-[var(--tf-muted)] mt-3 leading-relaxed">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </main>
      <footer className="px-6 py-4 flex justify-center text-[11px] text-[var(--tf-muted)]">
        <span className="inline-flex items-center gap-2"><ShieldCheck size={12} weight="duotone" /> {t("login_secure_session").split(" · ")[0]}</span>
      </footer>
    </div>
  );
}
